import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import { cachedInventoryRows } from '../janus-engine/inventory/cached-inventory-trees';
import { readInventoryField } from '../janus-engine/compile/inventory-raw-readers';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { encounterCreatureField, encounterField } from '../shared/array-types';
import { statusRows } from '../janus-engine/statuses/expiry';
import { sameJson } from '../janus-engine/compile/json-change';

type Row = Record<string, unknown>;
const row = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const readColumn = (table: string, source: Row, key: string): unknown => {
    if (table === 'encounters') return encounterField(source, key);
    if (table === 'encounter_creatures') return encounterCreatureField(source, key);
    if (table === 'inventory') return readInventoryField({ inventory: source }, key);
    return readItemField(source, key);
};

/** A new page has no operation ids. Its compiled snapshot nevertheless covers
 * saved columns. Find that coverage independently for each column: successive
 * writes can straddle the SELECT, and an older intent must not undo a saved one.
 * Retire only a wholly covered prefix: an older uncovered full-member frame can
 * restore earlier slots, so its later restoring frames must also be installed.
 * The remaining suffix uses the native installer and computed before witnesses. */
export function coveredFirstConnectFrames(frames: readonly unknown[], encounter: unknown): Set<number> {
    const covered = new Set<number>(), host = row(encounter);
    if (!host) return covered;
    const cells = new Map<string, Row>();
    const add = (table: string, id: unknown, source: Row | null): void => {
        if (id && source) cells.set(`${table}:${upper(id)}`, source);
    };
    add('encounters', encounterField(host, 'encounter_id'), row(host.encounters) ?? host);
    for (const node of cachedInventoryRows(host)) add('inventory', readInventoryField(node, 'inventory_id'), row(node.inventory));
    for (const member of Array.isArray(host.encounter_creatures_arr) ? host.encounter_creatures_arr : []) {
        const creature = row(member), ec = row(creature?.encounter_creatures);
        if (!creature || !ec) continue;
        add('encounter_creatures', encounterCreatureField(ec, 'encounter_creature_id'), ec);
        const pc = row(creature.player_characters);
        if (pc) add('player_characters', readItemField(pc, 'item_id'), pc);
        for (const side of ['target', 'caster'] as const) for (const status of statusRows(creature, side))
            add('statuses', readItemField(status, 'status_id'), status);
        for (const charge of Array.isArray(creature.charges) ? creature.charges : []) {
            const value = row(charge);
            if (value) add('charges', readItemField(value, 'charge_id'), value);
        }
    }
    const columns: string[][] = [], coverage = new Map<string, number>();
    frames.forEach((value, index) => {
        const wire = row(value), command = row(wire?.command);
        const applied = row(wire?.applied_frame ?? command?.applied_frame) as AppliedFrame | null;
        if (!applied || !Array.isArray(applied.rows) || !applied.rows.length || applied.new_entities?.length
            || applied.new_inventory?.length || applied.deletes?.length || wire?.correction || command?.correction) return;
        const keys: string[] = [];
        for (const change of applied.rows) {
            const identity = `${change.table}:${upper(change.row_id)}`, saved = cells.get(identity);
            for (const column of Object.keys(change.row)) {
                const key = `${identity}:${column}`;
                keys.push(key);
                if (!saved) continue;
                const actual = readColumn(change.table, saved, column), expected = readColumn(change.table, change.row, column);
                // Native compiled rows omit null, zero/empty values and quantity one.
                const leanDefault = column === 'item_qty' ? 1 : typeof expected === 'number' ? 0 : '';
                if (sameJson(actual ?? leanDefault, expected ?? leanDefault)) coverage.set(key, index);
            }
        }
        if (keys.length) columns[index] = keys;
    });
    for (let index = 0; index < frames.length; index++) {
        const keys = columns[index];
        if (!keys?.length || !keys.every(key => (coverage.get(key) ?? -1) >= index)) break;
        covered.add(index);
    }
    return covered;
}
